import { JMCLink } from "@components/JMCLink/JMCLink";
import { useBottomContent } from "@components/Layout/LogicSpacing/LogicSpacing";
import { SectionImpression } from "@components/SectionImpression/SectionImpression";
import { FileDownloadImpression } from "@jmc/core/src/components/FileDownloadImpression/index";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import { Color } from "@jmc/solid-design-system/src/components/molecules/HeaderHero/HeaderHero";
import { transformSpecialChars } from "@jmc/solid-design-system/src/utils/specialChars";
import useJnjBranding from "@jmc/utils/hooks/useJnjBranding";
import { default as fileHelper } from "@jmc/utils/utils/file-helper";
import { ApplicationState } from "@redux/modules";
import { BottomContentState, update } from "@redux/modules/bottomContent";
import { CMS_PROMOTIONAL_CONTENT, CMSProductProps, CMSSMPCProps, SMPCSource } from "@types";
import classnames from "classnames";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { graphql, useStaticQuery } from "gatsby";
import isEqual from "lodash/isEqual";
import React, { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { REQUEST_MATERIALS_URL, SMPC_URL } from "../../../plugins/JMCPageCreator/Constants";
import style from "./style.module.scss";

dayjs.extend(localizedFormat);

interface PropTypes {
    smpcs?: CMSSMPCProps[];
    products?: CMSProductProps[];
    marginXl?: boolean;
}

interface SMPCListProps {
    smpcs: CMSSMPCProps[];
    marginXl?: boolean;
}

export const MySMPCList = ({ smpcs, marginXl = false }: SMPCListProps): ReactElement => {
    const { t } = useTranslation();
    const { jnjFullBranded } = useJnjBranding();

    const hasFootnotesAbove: BottomContentState = useSelector(
        (state: ApplicationState) => state.bottomContent,
        isEqual,
    )?.hasVisibleFootnotes;

    const { lastSectionBackground } = useBottomContent();

    const hasTopSpacing = lastSectionBackground && lastSectionBackground !== Color.WHITE && !hasFootnotesAbove;

    if (smpcs && smpcs.length > 0) {
        return jnjFullBranded ? (
            <div className={classnames(style.smpcList, hasTopSpacing && style["top-spacing"])}>
                <Typography variant="h2" color="title" weight="400">
                    {t("SMPC & Technical Information", { ns: "common" })}
                </Typography>
                <div className={style.list}>
                    {smpcs.map((smpc: CMSSMPCProps) => {
                        const pageRef = smpc.page_reference?.[0];
                        const title = smpc.display_title || smpc.title;
                        if (smpc.smpc_source === SMPCSource.EXTERNAL && smpc.external_link) {
                            return (
                                <JMCLink
                                    fullWidth={false}
                                    name={title}
                                    key={smpc.uid}
                                    url={smpc.generate_detail_page ? `${SMPC_URL}/${smpc.uid}` : smpc.external_link}
                                    htmlLink
                                    commercial
                                    external={!smpc?.generate_detail_page}
                                    data-test-id={`SMPCItem-${title}`}
                                >
                                    <Typography color="inherit" variant="body-02">
                                        {transformSpecialChars(title)}
                                    </Typography>
                                </JMCLink>
                            );
                        } else if (smpc.smpc_source === SMPCSource.INTERNAL && pageRef) {
                            const url = pageRef?.page_url || `${REQUEST_MATERIALS_URL}/${pageRef?.material_id}`;
                            return (
                                <JMCLink
                                    fullWidth={false}
                                    name={title}
                                    data-test-id={`SMPCItem-${title}`}
                                    key={smpc.uid}
                                    url={url}
                                    htmlLink
                                    commercial={
                                        pageRef.regulatory_status
                                            ? pageRef.regulatory_status.promotional_or_medical ===
                                              CMS_PROMOTIONAL_CONTENT
                                            : true
                                    }
                                >
                                    <Typography color="inherit" variant="body-02">
                                        {transformSpecialChars(title)}
                                    </Typography>
                                </JMCLink>
                            );
                        } else if (smpc.smpc_source === SMPCSource.FILE_ASSET && smpc.file_asset) {
                            return (
                                <FileDownloadImpression
                                    key={smpc.uid}
                                    name={fileHelper.getFileName(smpc.file_asset.url)}
                                >
                                    <JMCLink
                                        fullWidth={false}
                                        url={
                                            smpc.generate_detail_page ? `${SMPC_URL}/${smpc.uid}` : smpc.file_asset.url
                                        }
                                        commercial
                                        external={!smpc?.generate_detail_page}
                                        htmlLink
                                        data-test-id={`SMPCItem-${title}`}
                                    >
                                        <Typography color="inherit" variant="body-02">
                                            {transformSpecialChars(title)}
                                        </Typography>
                                    </JMCLink>
                                </FileDownloadImpression>
                            );
                        }
                    })}
                </div>
            </div>
        ) : (
            <div className={classnames(style.smpcList, marginXl && style["smpcList-marginXl"])}>
                <Typography variant="h4" color="secondary" weight="500">
                    {t("SMPC & Technical Information", { ns: "common" })}
                </Typography>
                <div className={style.list}>
                    {smpcs.map((smpc: CMSSMPCProps) => {
                        const pageRef = smpc.page_reference?.[0];
                        const title = smpc.display_title || smpc.title;
                        if (smpc.smpc_source === SMPCSource.EXTERNAL && smpc.external_link) {
                            return (
                                <JMCLink
                                    fullWidth={false}
                                    name={title}
                                    key={smpc.uid}
                                    url={smpc.generate_detail_page ? `${SMPC_URL}/${smpc.uid}` : smpc.external_link}
                                    htmlLink
                                    commercial
                                    external={!smpc?.generate_detail_page}
                                    data-test-id={`SMPCItem-${title}`}
                                >
                                    {transformSpecialChars(title)}
                                </JMCLink>
                            );
                        } else if (smpc.smpc_source === SMPCSource.INTERNAL && pageRef) {
                            const url = pageRef?.page_url || `${REQUEST_MATERIALS_URL}/${pageRef?.material_id}`;
                            return (
                                <JMCLink
                                    fullWidth={false}
                                    name={title}
                                    data-test-id={`SMPCItem-${title}`}
                                    key={smpc.uid}
                                    url={url}
                                    htmlLink
                                    commercial={
                                        pageRef.regulatory_status
                                            ? pageRef.regulatory_status.promotional_or_medical ===
                                              CMS_PROMOTIONAL_CONTENT
                                            : true
                                    }
                                >
                                    {transformSpecialChars(title)}
                                </JMCLink>
                            );
                        } else if (smpc.smpc_source === SMPCSource.FILE_ASSET && smpc.file_asset) {
                            return (
                                <FileDownloadImpression
                                    key={smpc.uid}
                                    name={fileHelper.getFileName(smpc.file_asset.url)}
                                >
                                    <JMCLink
                                        fullWidth={false}
                                        url={
                                            smpc.generate_detail_page ? `${SMPC_URL}/${smpc.uid}` : smpc.file_asset.url
                                        }
                                        commercial
                                        external={!smpc?.generate_detail_page}
                                        htmlLink
                                        data-test-id={`SMPCItem-${title}`}
                                    >
                                        {transformSpecialChars(title)}
                                    </JMCLink>
                                </FileDownloadImpression>
                            );
                        }
                    })}
                </div>
            </div>
        );
    }
    return null;
};

export const SMPCList = ({ products = [], smpcs = [], marginXl = false }: PropTypes): JSX.Element => {
    const dispatch = useDispatch();
    const smpcProducts = useSelector(
        (state: ApplicationState) => state.smpcProducts as { products: CMSProductProps[]; refs: CMSSMPCProps[] },
        isEqual,
    );

    const { jnjFullBranded } = useJnjBranding();

    const { allContentstackSmpc } = useStaticQuery(graphql`
        query {
            allContentstackSmpc {
                nodes {
                    uid
                    title
                    display_title
                    smpc_source
                    external_link
                    generate_detail_page
                    file_asset {
                        url: transformedUrl
                    }
                    page_reference {
                        ... on Contentstack_page {
                            page_url: url
                            regulatory_status {
                                promotional_or_medical
                            }
                        }
                        ... on Contentstack_material {
                            material_id: uid
                            regulatory_status {
                                promotional_or_medical
                            }
                        }
                    }
                    exclude_from_automatic_smpc_collection
                    product_relation {
                        uid
                    }
                }
            }
        }
    `);

    const mergedSMPCsRefs = [...(smpcProducts?.refs ?? []), ...smpcs];
    const mergedSmpcProducts = [...(smpcProducts?.products ?? []), ...products];

    const getSMPCs = (): CMSSMPCProps[] => {
        const SMPCs = [
            ...mergedSMPCsRefs,
            ...(mergedSmpcProducts && mergedSmpcProducts.length > 0
                ? allContentstackSmpc.nodes.filter(
                      (smpc: CMSSMPCProps) =>
                          !smpc.exclude_from_automatic_smpc_collection &&
                          mergedSmpcProducts?.find((p) =>
                              smpc.product_relation.find((product) => p.uid === product.uid),
                          ),
                  )
                : []),
        ];

        // remove duplicates
        const uniqueSMPCs = Array.from(new Set(SMPCs.map((a) => a.uid))).map((uid) => {
            return SMPCs.find((a) => a.uid === uid);
        });

        return uniqueSMPCs.sort((a: CMSSMPCProps, b: CMSSMPCProps) => {
            const titleA = (a.display_title || a.title).toLowerCase();
            const titleB = (b.display_title || b.title).toLowerCase();

            return titleA < titleB ? -1 : titleA > titleB ? 1 : 0;
        });
    };

    const sortedSMPCs = getSMPCs();

    React.useEffect(() => {
        if (sortedSMPCs.length > 0 && jnjFullBranded) {
            dispatch(update({ hasVisibleSMPCs: true }));
        }
    }, [sortedSMPCs.length, dispatch]);

    return (
        <SectionImpression name={"SMPC & Technical Information"} position={1}>
            <MySMPCList smpcs={sortedSMPCs} marginXl={marginXl} />
        </SectionImpression>
    );
};
